import { ChevronDown, ChevronLeft, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useRealtime } from "@/realtime/RealtimeProvider";
import { useSession } from "@/session/SessionProvider";

import { useCashier } from "./CashierProvider";
import { CoinflowFrame, type CoinflowMessage } from "./CoinflowFrame";
import { CryptoDepositPane } from "./CryptoPanes";
import { NumberPad } from "./NumberPad";
import { paymentMethodRuntimeState } from "./payment-methods/availability";
import { PaymentMethodPicker } from "./payment-methods/PaymentMethodPicker";
import type { CashierActionResult } from "./types";

const QUICK_AMOUNTS = ["25", "100", "500", "3000"] as const;

/** Cashier method id → payments catalog method id (donor mapping). */
const LIMIT_METHOD_BY_ID: Record<string, string> = {
  card: "card",
  apple_pay: "apple_pay",
  googlepay: "google_pay",
};

const fmtLimitUsd = (value: string) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return null;
  return `$${parsed.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

type DepositView = "amount" | "iframe" | "processing" | "error";

const DEPOSIT_SETTLED_STATUSES = new Set(["completed", "captured", "settled"]);

export function DepositPane({
  onFullscreen,
}: {
  onFullscreen: (fullscreen: boolean) => void;
}) {
  const {
    adapter,
    adapterSnapshot,
    selectedWallet,
    applePay,
    refresh,
    closeCashier,
  } = useCashier();
  const { session } = useSession();
  const { subscribe } = useRealtime();

  const [view, setView] = useState<DepositView>("amount");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState<string | null>(null);
  const [methodError, setMethodError] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState<{
    url: string;
    sessionId: string | null;
  } | null>(null);
  const [currency, setCurrency] = useState("");
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const appleActiveRef = useRef(false);

  const cryptoAssets = useMemo(
    () => adapterSnapshot?.crypto?.assets ?? [],
    [adapterSnapshot?.crypto?.assets],
  );
  const methods = useMemo(
    () => adapterSnapshot?.depositMethods ?? [],
    [adapterSnapshot?.depositMethods],
  );
  const actionableMethods = useMemo(
    () =>
      methods.filter(
        (method) =>
          paymentMethodRuntimeState({
            adapter,
            applePay,
            cryptoAssets,
            flow: "deposit",
            method,
          }).actionable,
      ),
    [adapter, applePay, cryptoAssets, methods],
  );
  const effectiveMethodView =
    actionableMethods.find((method) => method.id === paymentMethod) ??
    actionableMethods.find((method) => method.id === "apple_pay") ??
    actionableMethods.find((method) => method.id === "card") ??
    actionableMethods[0] ??
    null;
  const effectiveMethod = effectiveMethodView?.id ?? "";
  const advertisedCurrencies =
    effectiveMethodView?.currencies?.filter(Boolean) ?? [];
  const supportedCurrencies = advertisedCurrencies.length
    ? advertisedCurrencies
    : selectedWallet?.currency
      ? [selectedWallet.currency]
      : [];
  const effectiveCurrency = supportedCurrencies.includes(currency)
    ? currency
    : (supportedCurrencies[0] ?? "");

  // Drive the dialog's fullscreen takeover from the iframe lifecycle.
  useEffect(() => {
    onFullscreen(view === "iframe");
    return () => onFullscreen(false);
  }, [onFullscreen, view]);

  const parsedAmount = Number.parseFloat(amount) || 0;
  const activeQuickAmount =
    QUICK_AMOUNTS.find((value) => Number.parseFloat(value) === parsedAmount) ??
    null;

  const catalogMethodId = LIMIT_METHOD_BY_ID[effectiveMethod];
  const methodLimit =
    catalogMethodId && effectiveCurrency === "USD"
      ? (adapterSnapshot?.depositLimits?.methods.find(
          (row) => row.id === catalogMethodId,
        ) ?? null)
      : null;
  const methodMinimum = methodLimit?.min
    ? Number.parseFloat(methodLimit.min)
    : null;
  const isValid =
    parsedAmount > 0 &&
    (methodMinimum === null ||
      !Number.isFinite(methodMinimum) ||
      parsedAmount >= methodMinimum);

  const boundsLabel = (() => {
    if (!methodLimit) return null;
    const parts: string[] = [];
    if (methodLimit.min) {
      const text = fmtLimitUsd(methodLimit.min);
      if (text) parts.push(`Min: ${text}`);
    }
    if (methodLimit.max) {
      const text = fmtLimitUsd(methodLimit.max);
      if (text) parts.push(`Max: ${text}`);
    }
    return parts.length ? parts.join(" · ") : null;
  })();

  const exceedsMethodMax = (() => {
    if (!methodLimit?.max || effectiveCurrency !== "USD") return false;
    const max = Number.parseFloat(methodLimit.max);
    return Number.isFinite(max) && parsedAmount > max;
  })();

  const settle = useCallback(() => {
    setView("amount");
    setAmount("");
    setCheckout(null);
    setNotice("Deposit received — your balance is updating.");
    void refresh();
  }, [refresh]);

  // Webhook-driven settlement signal (Apple Pay processing + iframe race).
  useEffect(() => {
    if (!session || (view !== "processing" && view !== "iframe")) return;
    return subscribe(`player.${session.playerId}`, (signal) => {
      if ("resync" in signal || signal.type !== "cashier.transaction") return;
      const payload = signal.payload as { kind?: string; status?: string };
      if (
        payload.kind === "deposit" &&
        DEPOSIT_SETTLED_STATUSES.has(payload.status ?? "")
      ) {
        settle();
      }
    });
  }, [session, settle, subscribe, view]);

  // Poll the operator session while the iframe is up — the postMessage can
  // be lost to a tab switch; the webhook-updated status is authoritative.
  useEffect(() => {
    if (
      view !== "iframe" ||
      !checkout?.sessionId ||
      !adapter?.getDepositSession
    ) {
      return;
    }
    const sessionId = checkout.sessionId;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      adapter
        .getDepositSession?.(sessionId, controller.signal)
        .then(({ status }) => {
          if (DEPOSIT_SETTLED_STATUSES.has(status)) settle();
          else if (status === "failed") {
            setCheckout(null);
            setFatalError("Payment failed. Please try again.");
            setView("error");
          }
        })
        .catch(() => undefined);
    }, 4000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [adapter, checkout?.sessionId, settle, view]);

  useEffect(
    () => () => {
      if (appleActiveRef.current) void adapter?.applePay?.cancelSession?.();
    },
    [adapter],
  );

  const selectAmount = (value: string) => {
    setAmount(value);
    setAmountError(null);
    setNotice(null);
  };

  const sanitizeKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const passthrough = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
    ];
    if (passthrough.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
    if (!/[0-9.]/.test(event.key)) event.preventDefault();
    if (event.key === "." && amount.includes(".")) event.preventDefault();
  };

  const sanitizeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) value = `${parts[0]}.${parts.slice(1).join("")}`;
    setAmount(value);
    setAmountError(null);
    setNotice(null);
  };

  const pressPadKey = (key: string) => {
    setAmountError(null);
    setNotice(null);
    setAmount((current) => {
      if (key === "back") return current.slice(0, -1);
      if (key === ".") {
        if (current.includes(".")) return current;
        return current ? `${current}.` : "0.";
      }
      const next = current + key;
      const [whole, decimals] = next.split(".");
      if (decimals !== undefined && decimals.length > 2) return current;
      if (whole.length > 6) return current;
      return next.replace(/^0+(?=\d)/, "");
    });
  };

  const requireValidAmount = () => {
    if (isValid) return true;
    const minimum =
      methodLimit?.min && fmtLimitUsd(methodLimit.min)
        ? fmtLimitUsd(methodLimit.min)
        : null;
    setAmountError(
      minimum ? `Minimum deposit is ${minimum}` : "Enter a valid amount",
    );
    return false;
  };

  const handleContinue = async () => {
    setAmountError(null);
    setMethodError(null);
    setNotice(null);
    if (
      effectiveMethod !== "card" ||
      !requireValidAmount() ||
      !adapter?.beginDeposit ||
      !selectedWallet ||
      !effectiveCurrency
    ) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await adapter.beginDeposit(
        {
          flow: "deposit",
          walletId: selectedWallet.id,
          methodId: effectiveMethod,
          amount: parsedAmount.toFixed(2),
          currency: effectiveCurrency,
          quote: null,
        },
        new AbortController().signal,
      );
      if (!result.redirectUrl) throw new Error("No checkout URL returned");
      setCheckout({
        url: result.redirectUrl,
        sessionId: result.sessionId ?? null,
      });
      setView("iframe");
    } catch (error) {
      setFatalError(
        error instanceof Error && error.message
          ? error.message
          : "Failed to initialize the checkout.",
      );
      setView("error");
    } finally {
      setSubmitting(false);
    }
  };

  const startApplePay = () => {
    setAmountError(null);
    setMethodError(null);
    setNotice(null);
    if (
      effectiveMethod !== "apple_pay" ||
      !requireValidAmount() ||
      !adapter?.applePay ||
      !selectedWallet ||
      !effectiveCurrency
    ) {
      return;
    }
    appleActiveRef.current = true;
    setSubmitting(true);
    void adapter.applePay.beginSession(
      {
        flow: "deposit",
        walletId: selectedWallet.id,
        methodId: "apple_pay",
        amount: parsedAmount.toFixed(2),
        currency: effectiveCurrency,
      },
      {
        onStatus: setMethodError,
        onComplete: (result: CashierActionResult) => {
          appleActiveRef.current = false;
          setSubmitting(false);
          setView("processing");
          void result;
        },
        onCancel: () => {
          appleActiveRef.current = false;
          setSubmitting(false);
        },
        onError: () => {
          appleActiveRef.current = false;
          setSubmitting(false);
          setMethodError(
            (current) =>
              current ??
              "Apple Pay is unavailable right now — you can pay by card instead.",
          );
        },
      },
    );
  };

  const startDirectMethod = async () => {
    setAmountError(null);
    setMethodError(null);
    setNotice(null);
    if (
      !requireValidAmount() ||
      !adapter?.beginDepositMethod ||
      !selectedWallet ||
      !effectiveMethod ||
      !effectiveCurrency
    ) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await adapter.beginDepositMethod(
        {
          flow: "deposit",
          walletId: selectedWallet.id,
          methodId: effectiveMethod,
          amount: parsedAmount.toFixed(2),
          currency: effectiveCurrency,
          quote: null,
        },
        new AbortController().signal,
      );
      if (result.redirectUrl) {
        setCheckout({
          url: result.redirectUrl,
          sessionId: result.sessionId ?? null,
        });
        setView("iframe");
      } else {
        setView("processing");
      }
    } catch (error) {
      setMethodError(
        error instanceof Error && error.message
          ? error.message
          : "This payment method is unavailable right now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFrameMessage = (message: CoinflowMessage) => {
    if (message.kind === "success") {
      settle();
    } else if (message.kind === "error") {
      setCheckout(null);
      setFatalError(message.message);
      setView("error");
    }
  };

  const exitToAmount = () => {
    setView("amount");
    setCheckout(null);
    setFatalError(null);
  };

  if (!adapter) {
    return (
      <div className="cashier-state">
        <strong>Cashier unavailable</strong>
        <p>No deposit request will be made.</p>
      </div>
    );
  }

  if (view === "error") {
    return (
      <div className="cashier-state">
        <strong>Payment failed</strong>
        <p>{fatalError ?? "Something went wrong. Please try again."}</p>
        <button
          className="cashier-state__retry"
          onClick={exitToAmount}
          type="button"
        >
          Try again
        </button>
      </div>
    );
  }

  if (view === "processing") {
    return (
      <div className="cashier-state" role="status">
        <span aria-hidden className="cashier-frame__spinner" />
        <strong>Processing your payment…</strong>
        <p>Hang tight — your balance will update in a few seconds.</p>
        <button
          className="cashier-state__retry"
          onClick={() => {
            setView("amount");
            setAmount("");
          }}
          type="button"
        >
          Close
        </button>
      </div>
    );
  }

  if (view === "iframe" && checkout) {
    return (
      <div className="cashier-takeover">
        <div className="cashier-takeover__bar">
          <button
            aria-label="Back"
            className="cashier-takeover__back"
            onClick={exitToAmount}
            type="button"
          >
            <ChevronLeft aria-hidden size={14} />
            <span>Back</span>
          </button>
          <span className="cashier-takeover__title">Complete Payment</span>
          <button
            aria-label="Close"
            className="cashier-takeover__close"
            onClick={closeCashier}
            type="button"
          >
            <X aria-hidden size={17} />
          </button>
        </div>
        <div className="cashier-takeover__body">
          <CoinflowFrame
            onMessage={handleFrameMessage}
            title="Coinflow Checkout"
            url={checkout.url}
          />
        </div>
      </div>
    );
  }

  const methodPicker = (
    <>
      <div className="cashier-section-label">Payment Method</div>
      <PaymentMethodPicker
        adapter={adapter}
        applePay={applePay}
        cryptoAssets={cryptoAssets}
        flow="deposit"
        methods={methods}
        onSelect={(methodId) => {
          setPaymentMethod(methodId);
          setMethodError(null);
          setCurrencyOpen(false);
        }}
        selectedId={effectiveMethod || null}
      />
    </>
  );

  if (effectiveMethod === "crypto") {
    return (
      <div className="cashier-pane">
        {methodPicker}
        <CryptoDepositPane />
      </div>
    );
  }

  if (!effectiveMethodView) {
    return (
      <div className="cashier-pane">
        {methodPicker}
        <div className="cashier-state">
          <strong>Payment methods unavailable</strong>
          <p>No deposit request will be made.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cashier-pane">
      {methodPicker}

      <div className="cashier-section-label">Amount</div>
      <div
        className="cashier-amount-row"
        data-error={amountError ? "" : undefined}
      >
        {effectiveCurrency === "USD" ? (
          <span aria-hidden className="cashier-amount-row__prefix">
            $
          </span>
        ) : null}
        <input
          aria-label="Deposit amount"
          autoComplete="off"
          inputMode="none"
          onChange={sanitizeInput}
          onKeyDown={sanitizeKeydown}
          placeholder="Enter Amount"
          type="text"
          value={amount}
        />
        {supportedCurrencies.length > 1 ? (
          <div className="cashier-currency">
            <button
              aria-expanded={currencyOpen}
              aria-haspopup="listbox"
              aria-label="Deposit currency"
              className="cashier-currency-chip"
              onClick={() => setCurrencyOpen((current) => !current)}
              type="button"
            >
              {effectiveCurrency}
              <ChevronDown
                aria-hidden
                data-open={currencyOpen || undefined}
                size={11}
              />
            </button>
            {currencyOpen ? (
              <div className="cashier-currency__menu" role="listbox">
                {supportedCurrencies
                  .filter((code) => code !== effectiveCurrency)
                  .map((code) => (
                    <button
                      aria-selected={false}
                      key={code}
                      onClick={() => {
                        setCurrency(code);
                        setCurrencyOpen(false);
                      }}
                      role="option"
                      type="button"
                    >
                      {code}
                    </button>
                  ))}
              </div>
            ) : null}
          </div>
        ) : (
          <span className="cashier-currency-chip is-static">
            {effectiveCurrency || "—"}
          </span>
        )}
      </div>

      <div className="cashier-hint-row">
        {amountError ? (
          <span className="cashier-hint is-error">{amountError}</span>
        ) : boundsLabel ? (
          <span
            className={`cashier-hint${exceedsMethodMax ? " is-error" : ""}`}
          >
            {boundsLabel}
          </span>
        ) : null}
      </div>

      <div className="cashier-quick-amounts">
        {QUICK_AMOUNTS.map((value) => (
          <button
            data-active={activeQuickAmount === value || undefined}
            key={value}
            onClick={() => selectAmount(value)}
            type="button"
          >
            ${value}
          </button>
        ))}
      </div>

      <div className="cashier-pane__footer">
        <NumberPad onKey={pressPadKey} />
        <div className="cashier-pane__cta">
          {methodError ? (
            <p className="cashier-method-error" role="alert">
              {methodError}
            </p>
          ) : null}
          {notice ? (
            <p className="cashier-notice" role="status">
              {notice}
            </p>
          ) : null}
          {effectiveMethod === "apple_pay" ? (
            <button
              aria-label="Pay with Apple Pay"
              className="cashier-apple-pay-button"
              disabled={!isValid || submitting}
              onClick={startApplePay}
              type="button"
            />
          ) : (
            <button
              className="cashier-cta"
              disabled={!isValid || submitting}
              onClick={() =>
                effectiveMethod === "card"
                  ? void handleContinue()
                  : void startDirectMethod()
              }
              type="button"
            >
              {submitting ? (
                <span aria-hidden className="cashier-cta__spinner" />
              ) : effectiveMethod === "card" ? (
                "Continue to Payment"
              ) : (
                `Continue with ${effectiveMethodView.label}`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
